import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStytchMember } from "@stytch/react/b2b";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { fetchApiPost } from "@/lib/api/utils";
import { useGetKnowledgeTags, KnowledgeApi, useCreateKnowledgeItem, useCreateKnowledgeUploadLink, useGetKnowledgePermissions, useUpdateKnowledgeItem, useGetStepTemplates } from "@/lib/api/knowledge";
import { ArrowLeft, Save } from "lucide-react";
import { GenerateLoadingOrError } from "@/lib/utils";
import { Combobox } from "@/components/ui/combobox";
import { Checkbox } from "@/components/ui/checkbox";

export function KnowledgeTaskCreate() {
    const navigate = useNavigate();
    const { member } = useStytchMember();
    const user_id: string = member?.trusted_metadata?.db_user_id as any;
    const permissionsListRes = useGetKnowledgePermissions();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedPermissionId, setSelectedPermissionId] = useState<string | null>(null);
    const [mainCategoryId, setMainCategoryId] = useState<string | null>(null);
    const [secondaryCategoryId, setSecondaryCategoryId] = useState<string | null>(null);
    const [title, setTitle] = useState("");
    const [itemType, setItemType] = useState<"task" | "common-block">("task");
    const [description, setDescription] = useState("");
    const [stepTemplateId, setStepTemplateId] = useState<string>("trainer_led");
    const createKnowledgeItem = useCreateKnowledgeItem();
    const tagsListRes = useGetKnowledgeTags(); // Fetch tags
    const stepTemplatesRes = useGetStepTemplates();

    const permissionsList = permissionsListRes.data?.data || [];
    const tagsList = tagsListRes.data?.data || [];
    const stepTemplates = stepTemplatesRes.data?.data || [];
    const selectedTemplate = stepTemplates.find((t: KnowledgeApi.StepTemplate) => t.id === stepTemplateId);

    const loadingOrErrorUi = GenerateLoadingOrError([permissionsListRes, tagsListRes, stepTemplatesRes]);

    if (loadingOrErrorUi) {
        return (
            <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
                <div className="flex items-center mb-6">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mr-2"
                        onClick={() => navigate("/tasks")}
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
                        Create Task
                    </h1>
                </div>
                {loadingOrErrorUi}
            </section>
        );
    }

    const handleSubmit = async (e: React.FormEvent) => {
        if (e?.preventDefault) {
            e.preventDefault();
        }

        if (!user_id) {
             toast({ title: "Error", description: "User not identified.", variant: "destructive" });
             return;
        }

        if (!!mainCategoryId && !!secondaryCategoryId && secondaryCategoryId === mainCategoryId) {
             toast({
                title: "Categories can not be the same",
                description: "",
            });
            return;
        }

        if (!title.trim()) {
            toast({
                title: "Error",
                description: "Please provide a title for the task.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await createKnowledgeItem({
                title,
                description: itemType === "task" ? description : "",
                content: "",
                owner_id: user_id,
                item_type: itemType,
                special_permission_id: selectedPermissionId || undefined,
                main_category_id: mainCategoryId || undefined,
                secondary_category_id: secondaryCategoryId || undefined,
                step_template_id: stepTemplateId as "trainer_led" | "self_attestation",
            });

            if (!result.success || !result?.data?.id) {
                 const errorMsg = result.message || "Failed to create task.";
                 console.error("Task creation failed:", errorMsg, result);
                 const displayMsg = typeof errorMsg === "string" && errorMsg.toLowerCase().includes("step_template_id")
                     ? "Invalid step template selected. Please refresh and try again."
                     : errorMsg;
                toast({
                    title: "Failed",
                    description: displayMsg,
                    variant: "destructive",
                });
                setIsSubmitting(false);
                return;
            }

            const newTaskId = result.data.id;
            navigate(`/tasks/${newTaskId}/edit`);
            
        } catch (error) {
            console.error("Error creating task:", error);
            toast({
                title: "Error",
                description: "An unexpected error occurred. Please try again.",
                variant: "destructive",
            });
             setIsSubmitting(false);
        }
    };

    return (
        <section className="flex min-h-full flex-1 flex-col justify-start px-6 lg:px-8">
            <div className="flex items-center mb-6">
                <Button
                    variant="ghost"
                    size="sm"
                    className="mr-2"
                    onClick={() => navigate("/tasks")}
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>
                <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
                    Create Task
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid gap-4">
                    <div className="grid gap-2">
                        <Label htmlFor="item-type">Is Resource</Label>
                        <Checkbox
                            id="item-type"
                            checked={itemType === "common-block"}
                            onCheckedChange={(checked) => setItemType(checked ? "common-block" : "task")}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Task title"
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    {itemType === "task" && (
                        <div className="grid gap-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description of this task"
                            rows={3}
                            disabled={isSubmitting}
                        />
                    </div>
                    )}

                    {itemType === "task" && stepTemplates.length > 0 && (
                        <div className="grid gap-2">
                            <Label htmlFor="step-template">Step Template</Label>
                            <Combobox
                                id="step-template"
                                dwimOnChange
                                value={stepTemplateId}
                                onValueChange={(value) => {
                                    if (value) {
                                        setStepTemplateId(value);
                                    }
                                }}
                                options={stepTemplates.map((template: KnowledgeApi.StepTemplate) => ({
                                    label: template.name,
                                    value: template.id,
                                }))}
                                className="w-[300px]"
                                popoverClassName="w-[300px]"
                                disabled={isSubmitting}
                            />
                            {selectedTemplate && selectedTemplate.steps.length > 0 && (
                                <div className="mt-2 rounded-md border p-3">
                                    <p className="text-sm font-medium text-muted-foreground mb-2">Steps that will be created:</p>
                                    <ul className="space-y-1">
                                        {selectedTemplate.steps.map((step: KnowledgeApi.StepTemplateStep) => (
                                            <li key={step.order} className="flex items-center justify-between text-sm">
                                                <span>{step.title}</span>
                                                <span className="text-muted-foreground text-xs capitalize">
                                                    completed by: {step.completion_by}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {(permissionsList.length > 0) && (
                        <div className="grid gap-2">
                            <Label htmlFor="special-permission">Special Permission</Label>
                            <Combobox
                                id="special-permission"
                                value={selectedPermissionId || ""}
                                onValueChange={(value) => setSelectedPermissionId(value === "" ? null : value)}
                                options={permissionsList.map((permission: { id: string, name: string }) => ({
                                    label: permission.name,
                                    value: permission.id,
                                }))}
                                className="w-[300px]"
                                popoverClassName="w-[300px]"
                                disabled={isSubmitting}
                            />
                        </div>
                    )}
                    {(tagsList.length > 0) && (
                        <div className="grid gap-2">
                            <Label htmlFor="special-permission">Main Category</Label>
                            <Combobox
                                id="main_category"
                                value={mainCategoryId || ""}
                                onValueChange={(value) => setMainCategoryId(value === "" ? null : value)}
                                options={tagsList.map((permission: { id: string, name: string }) => ({
                                    label: permission.name,
                                    value: permission.id,
                                }))}
                                className="w-[300px]"
                                popoverClassName="w-[300px]"
                                disabled={isSubmitting}
                            />
                        </div>
                    )}
                    {(tagsList.length > 0) && (
                        <div className="grid gap-2">
                            <Label htmlFor="special-permission">Secondary Category</Label>
                            <Combobox
                                id="secondary_category"
                                value={secondaryCategoryId || ""}
                                onValueChange={(value) => setSecondaryCategoryId(value === "" ? null : value)}
                                options={tagsList.filter((permission: { id: string, name: string }) => {
                                    if (!mainCategoryId) return true;
                                    return permission.id !== mainCategoryId;
                                }).map((permission: { id: string, name: string }) => ({
                                    label: permission.name,
                                    value: permission.id,
                                }))}
                                className="w-[300px]"
                                popoverClassName="w-[300px]"
                                disabled={isSubmitting}
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        className="mr-2"
                        onClick={() => navigate("/tasks")}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Creating..." : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Continue
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </section>
    );
}
